/**
 * CPU texture bakers. ARCHITECTURE.md §0.2 (nothing is loaded — every texel is computed),
 * §4 (`src/materials` may import `core` and `three`), §11.2 (everything returned is disposable).
 *
 * ---------------------------------------------------------------------------
 * The one rule that makes procedural surfaces look real
 * ---------------------------------------------------------------------------
 * Albedo, roughness, ambient occlusion, wear, dirt and the normal map are all derived from a
 * **single height field**. Generating six independent noise channels is what makes procedural
 * materials read as "noise sprayed on a plane": the speckles in the colour do not line up with
 * the bumps in the normal, so the eye gets no consistent surface to reconstruct.
 *
 * Here the pipeline is strictly ordered, and every later stage can see every earlier one:
 *
 *   1. height   H(u,v)                      — the surface, and the only free invention
 *   2. gradient dH/du, dH/dv  (Sobel, periodic wrap)   -> the normal map
 *   3. occlusion AO(H)        (multi-radius horizon)   -> shadowing in the crevices H created
 *   4. wear / dirt / wet      (may read H, AO, slope)  -> deposits obey the surface's shape
 *   5. albedo / roughness / metalness / emissive / alpha (may read all of the above)
 *
 * So dirt pools where the height field is low, wear rubs off where it is high and steep, AO
 * darkens exactly the pits the normal map shows, and roughness rises exactly where wear is.
 *
 * ---------------------------------------------------------------------------
 * Channel packing
 * ---------------------------------------------------------------------------
 * * `map`         RGB = albedo (sRGB encoded, tagged `SRGBColorSpace`), A = 1
 * * `normalMap`   RGB = tangent normal (OpenGL green-up), **A = height** — free, and available
 *                 to any future parallax/blend pass
 * * `ormMap`      R = occlusion, G = roughness, B = metalness, **A = wear** — one texture instead
 *                 of three, and the wear channel is what FX reads to tint sparks off worn metal
 * * `emissiveMap` RGB = emissive tint (sRGB), A = coverage
 * * `alphaMap`    all four channels = coverage (three samples `.g`; the rest cost nothing)
 *
 * Roughness and metalness are stored **relative to a declared range** (`roughRange`,
 * `metalRange`) because three multiplies the map by the material scalar
 * (`roughnessFactor *= texelRoughness.g`). The library sets the scalar to the range, so the
 * baked absolute values are reproduced exactly, and a caller's `roughnessBias` shifts the whole
 * surface with one uniform instead of forcing a rebake.
 */

import * as THREE from 'three';
import { clamp01, mix } from './noise.js';

/** Divisors allowed for per-map resolution reduction. */
const MAX_DOWNSAMPLE = 8;

// ---------------------------------------------------------------------------
// Colour space
// ---------------------------------------------------------------------------

/** Linear -> sRGB transfer function (IEC 61966-2-1). */
export function srgbEncode(linear) {
  const c = clamp01(linear);
  return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

/** sRGB -> linear transfer function. */
export function srgbDecode(encoded) {
  const c = clamp01(encoded);
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** 256-entry byte -> linear table so downsampling sRGB data stays gamma correct and cheap. */
const SRGB_TO_LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) SRGB_TO_LINEAR[i] = srgbDecode(i / 255);

/** 1025-entry linear -> byte table; the bakers call this once per channel per texel. */
const LINEAR_TO_SRGB_BYTE = new Uint8Array(1025);
for (let i = 0; i <= 1024; i++) {
  LINEAR_TO_SRGB_BYTE[i] = Math.round(srgbEncode(i / 1024) * 255);
}

/** Encodes a linear `[0,1]` value to an sRGB byte through the lookup table. */
export function linearToSrgbByte(v) {
  const t = v <= 0 ? 0 : v >= 1 ? 1024 : (v * 1024 + 0.5) | 0;
  return LINEAR_TO_SRGB_BYTE[t];
}

/** Clamps and rounds a `[0,1]` value to a byte with no transfer function applied. */
export function unitToByte(v) {
  return v <= 0 ? 0 : v >= 1 ? 255 : (v * 255 + 0.5) | 0;
}

// ---------------------------------------------------------------------------
// Field utilities
// ---------------------------------------------------------------------------

function wrapIdx(i, n) {
  if (i >= 0 && i < n) return i;
  const m = i % n;
  return m < 0 ? m + n : m;
}

/** Bilinear sample of a square scalar field in `[0,1]^2` UV space, with wrap-around. */
export function sampleField(field, size, u, v) {
  const fx = u * size - 0.5;
  const fy = v * size - 0.5;
  const x0 = Math.floor(fx);
  const y0 = Math.floor(fy);
  const tx = fx - x0;
  const ty = fy - y0;
  const xa = wrapIdx(x0, size);
  const xb = wrapIdx(x0 + 1, size);
  const ya = wrapIdx(y0, size) * size;
  const yb = wrapIdx(y0 + 1, size) * size;
  const top = mix(field[ya + xa], field[ya + xb], tx);
  const bot = mix(field[yb + xa], field[yb + xb], tx);
  return mix(top, bot, ty);
}

/** Nearest sample of a square scalar field by integer texel, with wrap-around. */
export function fieldAt(field, size, x, y) {
  return field[wrapIdx(y, size) * size + wrapIdx(x, size)];
}

/**
 * Scratch pool for the transient Float32 fields a bake needs. Bakes are sequential during
 * `init`, so reusing buffers keeps a 15-set library from churning a quarter of a gigabyte of
 * short-lived typed arrays through the collector on the loading screen.
 */
const SCRATCH = new Map();

function acquireField(size) {
  let stack = SCRATCH.get(size);
  if (!stack) {
    stack = [];
    SCRATCH.set(size, stack);
  }
  const buf = stack.pop();
  return buf || new Float32Array(size * size);
}

function releaseField(size, buf) {
  if (!buf) return;
  const stack = SCRATCH.get(size);
  if (stack && stack.length < 8) stack.push(buf);
}

/** Frees every pooled scratch buffer. Called by the library on dispose. */
export function clearScratchPool() {
  SCRATCH.clear();
}

// ---------------------------------------------------------------------------
// Derivatives and occlusion
// ---------------------------------------------------------------------------

/**
 * Periodic Sobel gradient of a height field, in **per-UV-unit** slope. Multiplying by `size`
 * converts the per-texel Sobel response into a derivative that is independent of resolution,
 * so the same recipe baked at 256 and at 1024 produces the same apparent bump depth.
 */
export function computeGradients(height, size, outDU, outDV) {
  for (let y = 0; y < size; y++) {
    const ym = wrapIdx(y - 1, size) * size;
    const y0 = y * size;
    const yp = wrapIdx(y + 1, size) * size;
    for (let x = 0; x < size; x++) {
      const xm = wrapIdx(x - 1, size);
      const xp = wrapIdx(x + 1, size);
      const hmm = height[ym + xm];
      const hm0 = height[ym + x];
      const hmp = height[ym + xp];
      const h0m = height[y0 + xm];
      const h0p = height[y0 + xp];
      const hpm = height[yp + xm];
      const hp0 = height[yp + x];
      const hpp = height[yp + xp];
      const gx = hmp + 2 * h0p + hpp - (hmm + 2 * h0m + hpm);
      const gy = hpm + 2 * hp0 + hpp - (hmm + 2 * hm0 + hmp);
      const i = y0 + x;
      outDU[i] = (gx / 8) * size;
      outDV[i] = (gy / 8) * size;
    }
  }
}

/** Periodic discrete Laplacian in per-UV² units — positive in pits, negative on ridges. */
export function curvatureAt(height, size, x, y) {
  const c = fieldAt(height, size, x, y);
  const s =
    fieldAt(height, size, x - 1, y) +
    fieldAt(height, size, x + 1, y) +
    fieldAt(height, size, x, y - 1) +
    fieldAt(height, size, x, y + 1);
  return (s - 4 * c) * size;
}

// 8 evenly spaced tap directions, precomputed.
const AO_DIRS = new Float32Array(16);
for (let i = 0; i < 8; i++) {
  const a = (i / 8) * Math.PI * 2 + 0.19634954; // offset so no tap is axis-aligned
  AO_DIRS[i * 2] = Math.cos(a);
  AO_DIRS[i * 2 + 1] = Math.sin(a);
}
// Three radii in texels with descending weight: a broad soft term plus two contact terms.
const AO_RADII = new Float32Array([2, 5, 11]);
const AO_WEIGHTS = new Float32Array([0.45, 0.33, 0.22]);

/**
 * Horizon-based ambient occlusion straight from the height field. For each tap the local
 * elevation `tan = dH * heightScale / distance` is accumulated; the mean is the fraction of the
 * hemisphere blocked by the surface's own relief.
 *
 * Baked at `size / aoDivisor` because occlusion is inherently low frequency — a quarter of the
 * texels carries all of the signal, and the bilinear upsample in the shading pass is free.
 *
 * @param {Float32Array} height
 * @param {number} size resolution of `height`
 * @param {object} opt `{ heightScale, strength, radiusScale, divisor }`
 * @returns {{ field: Float32Array, size: number }}
 */
export function computeAO(height, size, opt) {
  const divisor = Math.max(1, Math.min(MAX_DOWNSAMPLE, Math.round(opt.divisor || 2)));
  const aoSize = Math.max(16, (size / divisor) | 0);
  const strength = opt.strength !== undefined ? opt.strength : 1;
  const heightScale = opt.heightScale !== undefined ? opt.heightScale : 0.02;
  const radiusScale = opt.radiusScale !== undefined ? opt.radiusScale : 1;
  const field = acquireField(aoSize);
  const invSize = 1 / size;
  for (let ay = 0; ay < aoSize; ay++) {
    const v = (ay + 0.5) / aoSize;
    const hy = v * size - 0.5;
    for (let ax = 0; ax < aoSize; ax++) {
      const u = (ax + 0.5) / aoSize;
      const hx = u * size - 0.5;
      const hc = sampleField(height, size, u, v);
      let occ = 0;
      for (let r = 0; r < AO_RADII.length; r++) {
        const radiusTexels = AO_RADII[r] * radiusScale;
        const distUv = radiusTexels * invSize;
        const invDist = distUv > 0 ? 1 / distUv : 0;
        let sum = 0;
        for (let d = 0; d < 8; d++) {
          const nx = hx + AO_DIRS[d * 2] * radiusTexels;
          const ny = hy + AO_DIRS[d * 2 + 1] * radiusTexels;
          const hn = sampleField(height, size, (nx + 0.5) * invSize, (ny + 0.5) * invSize);
          const tan = (hn - hc) * heightScale * invDist;
          if (tan > 0) sum += tan > 1 ? 1 : tan;
        }
        occ += (sum / 8) * AO_WEIGHTS[r];
      }
      field[ay * aoSize + ax] = clamp01(1 - occ * strength);
    }
  }
  return { field, size: aoSize };
}

// ---------------------------------------------------------------------------
// Texture creation
// ---------------------------------------------------------------------------

/**
 * Wraps a byte buffer in a `THREE.DataTexture` configured for tiling PBR use.
 *
 * `DataTexture` defaults to `NearestFilter` with no mipmaps and `ClampToEdgeWrapping`, all of
 * which are wrong for a tiling surface map, so every field is set explicitly.
 *
 * @param {Uint8Array} bytes RGBA, `size*size*4`
 * @param {number} size
 * @param {object} [opt]
 * @param {boolean} [opt.srgb] tag as sRGB (albedo, emissive) rather than raw data
 * @param {number} [opt.anisotropy]
 * @param {number} [opt.repeat] uniform UV repeat
 * @param {number} [opt.repeatX]
 * @param {number} [opt.repeatY]
 * @param {boolean} [opt.mipmaps]
 * @param {string} [opt.name]
 * @param {boolean} [opt.clamp] clamp instead of repeat (signage, decals)
 */
export function createTexture(bytes, size, opt = {}) {
  const tex = new THREE.DataTexture(bytes, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.colorSpace = opt.srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
  const wrap = opt.clamp ? THREE.ClampToEdgeWrapping : THREE.RepeatWrapping;
  tex.wrapS = wrap;
  tex.wrapT = wrap;
  const mips = opt.mipmaps !== false;
  tex.generateMipmaps = mips;
  tex.minFilter = mips ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.anisotropy = Math.max(1, Math.round(opt.anisotropy || 1));
  const rx = opt.repeatX !== undefined ? opt.repeatX : opt.repeat !== undefined ? opt.repeat : 1;
  const ry = opt.repeatY !== undefined ? opt.repeatY : opt.repeat !== undefined ? opt.repeat : 1;
  tex.repeat.set(rx, ry);
  tex.name = opt.name || 'coi-baked';
  tex.needsUpdate = true;
  return tex;
}

/**
 * GPU bytes a texture occupies, including the mip chain. Mipmapped RGBA8 costs 4/3 of the base
 * level, which is the number that matters for the ~48 MB budget in the brief.
 */
export function estimateTextureBytes(tex) {
  if (!tex || !tex.image) return 0;
  const w = tex.image.width || 0;
  const h = tex.image.height || 0;
  const base = w * h * 4;
  return tex.generateMipmaps ? Math.round((base * 4) / 3) : base;
}

/**
 * Box-downsamples an RGBA buffer by an integer factor. When `srgb` is set the RGB channels are
 * decoded to linear before averaging and re-encoded after, because averaging sRGB bytes directly
 * darkens every downsample.
 */
export function downsampleRGBA(src, srcSize, factor, srgb) {
  const f = Math.max(1, Math.round(factor));
  if (f === 1) return src;
  const dstSize = Math.max(1, (srcSize / f) | 0);
  const dst = new Uint8Array(dstSize * dstSize * 4);
  const inv = 1 / (f * f);
  for (let y = 0; y < dstSize; y++) {
    for (let x = 0; x < dstSize; x++) {
      let r = 0;
      let g = 0;
      let b = 0;
      let a = 0;
      for (let sy = 0; sy < f; sy++) {
        const row = ((y * f + sy) * srcSize + x * f) * 4;
        for (let sx = 0; sx < f; sx++) {
          const i = row + sx * 4;
          if (srgb) {
            r += SRGB_TO_LINEAR[src[i]];
            g += SRGB_TO_LINEAR[src[i + 1]];
            b += SRGB_TO_LINEAR[src[i + 2]];
          } else {
            r += src[i];
            g += src[i + 1];
            b += src[i + 2];
          }
          a += src[i + 3];
        }
      }
      const o = (y * dstSize + x) * 4;
      if (srgb) {
        dst[o] = linearToSrgbByte(r * inv);
        dst[o + 1] = linearToSrgbByte(g * inv);
        dst[o + 2] = linearToSrgbByte(b * inv);
      } else {
        dst[o] = (r * inv + 0.5) | 0;
        dst[o + 1] = (g * inv + 0.5) | 0;
        dst[o + 2] = (b * inv + 0.5) | 0;
      }
      dst[o + 3] = (a * inv + 0.5) | 0;
    }
  }
  return dst;
}

// ---------------------------------------------------------------------------
// The surface baker
// ---------------------------------------------------------------------------

/** The mutable record every author callback receives. One instance per bake; never retained. */
function createSample() {
  return {
    u: 0,
    v: 0,
    x: 0,
    y: 0,
    size: 1,
    seed: 0,
    /** Height field value at this texel. */
    h: 0,
    /** Per-UV height derivatives, already scaled by `normal.heightScale`. */
    dhdu: 0,
    dhdv: 0,
    /** `sqrt(dhdu^2 + dhdv^2)` — how steep the surface is here. */
    slope: 0,
    /** Laplacian; positive in pits, negative on ridges. Drives edge wear. */
    curvature: 0,
    /** Baked ambient occlusion, `[0,1]`, 1 = open. */
    ao: 0,
    /** Auxiliary masks, `[0,1]`. Zero unless the recipe supplies a generator. */
    wear: 0,
    dirt: 0,
    wet: 0,
  };
}

const RGB_SCRATCH = new Float32Array(3);
const RGB_SCRATCH_2 = new Float32Array(3);

function outputDivisor(outputs, key, fallback) {
  const v = outputs && outputs[key] !== undefined ? outputs[key] : fallback;
  if (v === 0 || v === false) return 0;
  return Math.max(1, Math.min(MAX_DOWNSAMPLE, Math.round(v === true ? 1 : v)));
}

/**
 * Bakes one coherent surface: a height field plus every PBR channel derived from it.
 *
 * @param {object} spec
 * @param {string}  spec.name              debug name; suffixed per map
 * @param {number}  spec.size              base resolution (power of two)
 * @param {number}  [spec.seed]            integer seed handed to the recipe callbacks
 * @param {(u:number,v:number,s:object)=>number} spec.height   the surface, nominally `[0,1]`
 * @param {(u:number,v:number,s:object)=>number} [spec.wear]   evaluated after H, AO and slope
 * @param {(u:number,v:number,s:object)=>number} [spec.dirt]
 * @param {(u:number,v:number,s:object)=>number} [spec.wet]
 * @param {(out:Float32Array,s:object)=>void}    [spec.albedo] linear RGB
 * @param {(s:object)=>number}                   [spec.roughness] absolute `[0,1]`
 * @param {(s:object)=>number}                   [spec.metalness] absolute `[0,1]`
 * @param {(out:Float32Array,s:object)=>void}    [spec.emissive] linear RGB
 * @param {(s:object)=>number}                   [spec.alpha]
 * @param {number}  [spec.roughRange]      declared maximum roughness; the map stores `abs/range`
 * @param {number}  [spec.metalRange]      declared maximum metalness
 * @param {object}  [spec.normal]          `{ heightScale, strength }`
 * @param {object}  [spec.ao]              `{ strength, radiusScale, divisor }`
 * @param {object}  [spec.outputs]         per-map resolution divisor; 0 = do not produce
 * @param {number}  [spec.anisotropy]
 * @param {number}  [spec.repeat]
 * @param {boolean} [spec.retainHeight]    keep a copy of the height field in the result
 * @returns {object} `{ name, size, map, normalMap, ormMap, emissiveMap, alphaMap, textures,
 *                      bytes, roughRange, metalRange, heightField, stats }`
 */
export function bakeSurface(spec) {
  const size = Math.max(8, Math.round(spec.size));
  const seed = (spec.seed || 0) | 0;
  const texels = size * size;
  const outputs = spec.outputs || {};
  const normalOpt = spec.normal || {};
  const heightScale = normalOpt.heightScale !== undefined ? normalOpt.heightScale : 0.02;
  const normalStrength = normalOpt.strength !== undefined ? normalOpt.strength : 1;
  const roughRange = spec.roughRange !== undefined ? spec.roughRange : 1;
  const metalRange = spec.metalRange !== undefined ? spec.metalRange : 1;
  const anisotropy = spec.anisotropy || 1;
  const repeat = spec.repeat !== undefined ? spec.repeat : 1;

  const divMap = spec.albedo ? outputDivisor(outputs, 'map', 1) : 0;
  const divNormal = outputDivisor(outputs, 'normalMap', 1);
  const divOrm = spec.roughness || spec.metalness ? outputDivisor(outputs, 'ormMap', 2) : 0;
  const divEmissive = spec.emissive ? outputDivisor(outputs, 'emissiveMap', 1) : 0;
  const divAlpha = spec.alpha ? outputDivisor(outputs, 'alphaMap', 1) : 0;

  // --- stage 1: height ---
  const H = acquireField(size);
  const s = createSample();
  s.size = size;
  s.seed = seed;
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      s.u = u;
      s.v = v;
      s.x = x;
      s.y = y;
      H[y * size + x] = spec.height(u, v, s);
    }
  }

  // --- stage 2: gradients ---
  const DU = acquireField(size);
  const DV = acquireField(size);
  computeGradients(H, size, DU, DV);

  // --- stage 3: occlusion ---
  const aoResult = computeAO(H, size, {
    heightScale,
    strength: spec.ao && spec.ao.strength !== undefined ? spec.ao.strength : 1,
    radiusScale: spec.ao && spec.ao.radiusScale !== undefined ? spec.ao.radiusScale : 1,
    divisor: spec.ao && spec.ao.divisor !== undefined ? spec.ao.divisor : 2,
  });

  // --- stage 4: auxiliary masks (may read H, gradients, AO) ---
  const needWear = typeof spec.wear === 'function';
  const needDirt = typeof spec.dirt === 'function';
  const needWet = typeof spec.wet === 'function';
  const WEAR = needWear ? acquireField(size) : null;
  const DIRT = needDirt ? acquireField(size) : null;
  const WET = needWet ? acquireField(size) : null;

  const fillSample = (x, y) => {
    const i = y * size + x;
    const u = (x + 0.5) / size;
    const v = (y + 0.5) / size;
    s.u = u;
    s.v = v;
    s.x = x;
    s.y = y;
    s.h = H[i];
    const du = DU[i] * heightScale;
    const dv = DV[i] * heightScale;
    s.dhdu = du;
    s.dhdv = dv;
    s.slope = Math.sqrt(du * du + dv * dv);
    s.ao = sampleField(aoResult.field, aoResult.size, u, v);
    return i;
  };

  if (needWear || needDirt || needWet) {
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const i = fillSample(x, y);
        s.curvature = curvatureAt(H, size, x, y);
        s.wear = 0;
        s.dirt = 0;
        s.wet = 0;
        if (needWear) {
          s.wear = clamp01(spec.wear(s.u, s.v, s));
          WEAR[i] = s.wear;
        }
        if (needDirt) {
          s.dirt = clamp01(spec.dirt(s.u, s.v, s));
          DIRT[i] = s.dirt;
        }
        if (needWet) {
          s.wet = clamp01(spec.wet(s.u, s.v, s));
          WET[i] = s.wet;
        }
      }
    }
  }

  // --- stage 5: channel outputs ---
  const albedoBytes = divMap ? new Uint8Array(texels * 4) : null;
  const normalBytes = divNormal ? new Uint8Array(texels * 4) : null;
  const ormBytes = divOrm ? new Uint8Array(texels * 4) : null;
  const emissiveBytes = divEmissive ? new Uint8Array(texels * 4) : null;
  const alphaBytes = divAlpha ? new Uint8Array(texels * 4) : null;

  const invRough = roughRange > 0 ? 1 / roughRange : 0;
  const invMetal = metalRange > 0 ? 1 / metalRange : 0;
  let roughClipped = 0;
  let metalClipped = 0;
  let hMin = Infinity;
  let hMax = -Infinity;

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = fillSample(x, y);
      s.curvature = curvatureAt(H, size, x, y);
      s.wear = WEAR ? WEAR[i] : 0;
      s.dirt = DIRT ? DIRT[i] : 0;
      s.wet = WET ? WET[i] : 0;
      if (s.h < hMin) hMin = s.h;
      if (s.h > hMax) hMax = s.h;
      const o = i * 4;

      if (albedoBytes) {
        RGB_SCRATCH[0] = 0.5;
        RGB_SCRATCH[1] = 0.5;
        RGB_SCRATCH[2] = 0.5;
        spec.albedo(RGB_SCRATCH, s);
        albedoBytes[o] = linearToSrgbByte(RGB_SCRATCH[0]);
        albedoBytes[o + 1] = linearToSrgbByte(RGB_SCRATCH[1]);
        albedoBytes[o + 2] = linearToSrgbByte(RGB_SCRATCH[2]);
        albedoBytes[o + 3] = 255;
      }

      if (normalBytes) {
        const nx = -s.dhdu * normalStrength;
        const ny = -s.dhdv * normalStrength;
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
        normalBytes[o] = unitToByte(nx * inv * 0.5 + 0.5);
        normalBytes[o + 1] = unitToByte(ny * inv * 0.5 + 0.5);
        normalBytes[o + 2] = unitToByte(inv * 0.5 + 0.5);
        normalBytes[o + 3] = unitToByte(s.h);
      }

      if (ormBytes) {
        const absRough = spec.roughness ? clamp01(spec.roughness(s)) : roughRange;
        const absMetal = spec.metalness ? clamp01(spec.metalness(s)) : 0;
        const relRough = absRough * invRough;
        const relMetal = absMetal * invMetal;
        if (relRough > 1.0001) roughClipped++;
        if (relMetal > 1.0001) metalClipped++;
        ormBytes[o] = unitToByte(s.ao);
        ormBytes[o + 1] = unitToByte(relRough);
        ormBytes[o + 2] = unitToByte(relMetal);
        ormBytes[o + 3] = unitToByte(s.wear);
      }

      if (emissiveBytes) {
        RGB_SCRATCH_2[0] = 0;
        RGB_SCRATCH_2[1] = 0;
        RGB_SCRATCH_2[2] = 0;
        spec.emissive(RGB_SCRATCH_2, s);
        emissiveBytes[o] = linearToSrgbByte(RGB_SCRATCH_2[0]);
        emissiveBytes[o + 1] = linearToSrgbByte(RGB_SCRATCH_2[1]);
        emissiveBytes[o + 2] = linearToSrgbByte(RGB_SCRATCH_2[2]);
        emissiveBytes[o + 3] = 255;
      }

      if (alphaBytes) {
        const a = unitToByte(clamp01(spec.alpha(s)));
        alphaBytes[o] = a;
        alphaBytes[o + 1] = a;
        alphaBytes[o + 2] = a;
        alphaBytes[o + 3] = a;
      }
    }
  }

  const name = spec.name || 'surface';
  const textures = [];
  const makeMap = (bytes, divisor, srgb, suffix) => {
    if (!bytes) return null;
    const data = downsampleRGBA(bytes, size, divisor, srgb);
    const outSize = Math.max(1, (size / divisor) | 0);
    const tex = createTexture(data, outSize, {
      srgb,
      anisotropy,
      repeat,
      name: `${name}.${suffix}`,
    });
    textures.push(tex);
    return tex;
  };

  const map = makeMap(albedoBytes, divMap, true, 'albedo');
  const normalMap = makeMap(normalBytes, divNormal, false, 'normal');
  const ormMap = makeMap(ormBytes, divOrm, false, 'orm');
  const emissiveMap = makeMap(emissiveBytes, divEmissive, true, 'emissive');
  const alphaMap = makeMap(alphaBytes, divAlpha, false, 'alpha');

  let heightField = null;
  if (spec.retainHeight) {
    heightField = new Float32Array(H);
  }

  let bytes = 0;
  for (const t of textures) bytes += estimateTextureBytes(t);

  releaseField(size, H);
  releaseField(size, DU);
  releaseField(size, DV);
  releaseField(aoResult.size, aoResult.field);
  if (WEAR) releaseField(size, WEAR);
  if (DIRT) releaseField(size, DIRT);
  if (WET) releaseField(size, WET);

  return {
    name,
    size,
    map,
    normalMap,
    ormMap,
    emissiveMap,
    alphaMap,
    textures,
    bytes,
    roughRange,
    metalRange,
    heightField,
    stats: {
      texels,
      heightMin: hMin,
      heightMax: hMax,
      roughClipped,
      metalClipped,
      aoSize: aoResult.size,
    },
  };
}

/**
 * Bakes a standalone RGBA texture from a per-texel callback. Used for masks, ramps and the
 * glyph atlas — anything that is not a full PBR surface.
 *
 * @param {object} spec `{ name, size, srgb, clamp, anisotropy, repeat, mipmaps }`
 * @param {(out:Float32Array, u:number, v:number, x:number, y:number)=>void} spec.rgba
 *        writes linear RGB into `out[0..2]` and coverage into `out[3]`
 */
export function bakeTexture(spec) {
  const size = Math.max(4, Math.round(spec.size));
  const bytes = new Uint8Array(size * size * 4);
  const out = new Float32Array(4);
  const srgb = spec.srgb === true;
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      out[0] = 0;
      out[1] = 0;
      out[2] = 0;
      out[3] = 1;
      spec.rgba(out, u, v, x, y);
      const o = (y * size + x) * 4;
      if (srgb) {
        bytes[o] = linearToSrgbByte(out[0]);
        bytes[o + 1] = linearToSrgbByte(out[1]);
        bytes[o + 2] = linearToSrgbByte(out[2]);
      } else {
        bytes[o] = unitToByte(out[0]);
        bytes[o + 1] = unitToByte(out[1]);
        bytes[o + 2] = unitToByte(out[2]);
      }
      bytes[o + 3] = unitToByte(out[3]);
    }
  }
  return createTexture(bytes, size, {
    srgb,
    clamp: spec.clamp === true,
    anisotropy: spec.anisotropy || 1,
    repeat: spec.repeat !== undefined ? spec.repeat : 1,
    mipmaps: spec.mipmaps !== false,
    name: spec.name || 'coi-texture',
  });
}

/**
 * Bakes a normal map directly from a height callback, with no albedo or ORM. Cheaper than a
 * full `bakeSurface` for water ripples and glass imperfection, where colour is a constant.
 */
export function bakeNormalOnly(spec) {
  const size = Math.max(8, Math.round(spec.size));
  const H = acquireField(size);
  const s = createSample();
  s.size = size;
  s.seed = (spec.seed || 0) | 0;
  for (let y = 0; y < size; y++) {
    const v = (y + 0.5) / size;
    for (let x = 0; x < size; x++) {
      const u = (x + 0.5) / size;
      s.u = u;
      s.v = v;
      s.x = x;
      s.y = y;
      H[y * size + x] = spec.height(u, v, s);
    }
  }
  const DU = acquireField(size);
  const DV = acquireField(size);
  computeGradients(H, size, DU, DV);
  const heightScale = spec.heightScale !== undefined ? spec.heightScale : 0.02;
  const strength = spec.strength !== undefined ? spec.strength : 1;
  const bytes = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    const nx = -DU[i] * heightScale * strength;
    const ny = -DV[i] * heightScale * strength;
    const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1);
    const o = i * 4;
    bytes[o] = unitToByte(nx * inv * 0.5 + 0.5);
    bytes[o + 1] = unitToByte(ny * inv * 0.5 + 0.5);
    bytes[o + 2] = unitToByte(inv * 0.5 + 0.5);
    bytes[o + 3] = unitToByte(H[i]);
  }
  releaseField(size, H);
  releaseField(size, DU);
  releaseField(size, DV);
  const divisor = Math.max(1, Math.round(spec.divisor || 1));
  const data = downsampleRGBA(bytes, size, divisor, false);
  return createTexture(data, Math.max(1, (size / divisor) | 0), {
    srgb: false,
    anisotropy: spec.anisotropy || 1,
    repeat: spec.repeat !== undefined ? spec.repeat : 1,
    name: `${spec.name || 'surface'}.normal`,
  });
}
